/*
 * @Description: 商品处理相关的公共方法
 * @Author: 王逢超 wangfengchao@nextmar.com
 * @Date: 2022-07-12 09:29:58
 */
import type { ProductsListImage, ProductsListItem } from '@/api/types/product';

/** checkAcc / checkGlasses 的裁切参数 */
interface CheckListResizeOptions {
  isResize?: boolean;
  width?: number;
  imageTypes?: (keyof ProductsListImage)[];
}

interface CheckListResizeOptionsFull {
  isResize: boolean;
  width: number;
  imageTypes: (keyof ProductsListImage)[];
}

/** transformAdp 的返回值 */
interface TransformAdpResult {
  order: string;
  top_shop_skus: string | string[];
  adp: string | undefined;
}

/** 可展示镜片类型配置项 */
interface AvailableLensItem {
  name: string;
  shortName: string;
  desc: string;
  imgClass: string;
  link: string;
}

/**
 * 为商品列表每两项一组标记是否含配件（has_acc），并可选对图片做 akamai 重设
 * @param list 商品列表（会被原地修改，添加 has_acc；若 isResize 为 true 会改写 image.material/product）
 * @param isResize 是否对 image.material、image.product 做 akamai 重设
 */
export const checkAcc = function (
  list: ProductsListItem[],
  num = 4,
  resizeOptions: CheckListResizeOptions = { isResize: false },
) {
  let has_acc = false;
  const options: CheckListResizeOptionsFull = Object.assign(
    { isResize: false, width: 750, imageTypes: ['product', 'material'] },
    resizeOptions,
  );
  list.forEach((item, idx) => {
    if (idx % num === 0) {
      has_acc = false;
      /* 检查一行首项是否为非镜框 */
      if (item.is_glass === 2) {
        has_acc = true;
      } else {
        /* 检查一行后面几个项是否为非镜框 */
        let count = num - 1;
        while (count > 0) {
          if (list[idx + count] && list[idx + count].is_glass === 2) {
            has_acc = true;
            break;
          }
          count--;
        }
      }
      item.has_acc = has_acc;
    } else {
      item.has_acc = has_acc;
    }
    if (options.isResize && options.width > 0) {
      options.imageTypes.forEach((type: keyof ProductsListImage) => {
        const arr = item.image?.[type];
        if (item.image && arr?.map) {
          item.image[type] = arr.map((src: string) => `${src}?im=Resize,width=${options.width}`);
        }
      });
    }
  });
};
/**
 * 列表类接口对商品列表做处理。1. 判断一行是否有眼镜；2. 对图片进行裁切
 * @param list 需要处理的列表
 * @param num 一行展示的商品数量
 * @param resizeOptions 裁切参数，默认 isResize: false, width: 401, imageTypes: ['product','material']
 * @returns 处理后的列表（原列表）
 */
export const checkGlasses = function (
  list: ProductsListItem[],
  num = 4,
  resizeOptions: CheckListResizeOptions = { isResize: false },
) {
  let has_glasses = false;
  const options: CheckListResizeOptionsFull = Object.assign(
    { isResize: false, width: 750, imageTypes: ['product', 'material'] },
    resizeOptions,
  );
  list.forEach((item, idx) => {
    if (idx % num === 0) {
      has_glasses = false;
      /* 检查一行首项是否为非镜框 */
      if (item.is_glass === 1) {
        has_glasses = true;
      } else {
        /* 检查一行后面几个项是否为非镜框 */
        let count = num - 1;
        while (count > 0) {
          if (list[idx + count] && list[idx + count].is_glass === 1) {
            has_glasses = true;
            break;
          }
          count--;
        }
      }
      item.has_glasses = has_glasses;
    } else {
      item.has_glasses = has_glasses;
    }
    if (options.isResize && options.width > 0) {
      options.imageTypes.forEach((type: keyof ProductsListImage) => {
        const arr = item.image?.[type];
        if (item.image && arr?.map) {
          item.image[type] = arr.map((src: string) => `${src}?im=Resize,width=${options.width}`);
        }
      });
    }
  });
};

/** 首页预览路由 */
export const homePreviewPath = '/fd215b1b069e93dbd2bcf18eb57ac0ac';
/** 新品专题页预览路由 */
export const BEAUTY_PREVIEW_PATH = '/33t1ajet7o3o57e5';
/** 极光专题页预览路由 */
export const AURORA_COLLECTION_PATH = '/297c6df1ba034685';
/** 霓虹专题页预览路由 */
export const NEON_COLLECTION_PATH = '/6cc22c90172bd519';
/** 防蓝光专题页预览路由 */
export const BLUE_LIGHT_COLLECTION_PATH = '/082477682908446b';
/** 渐进镜片专题页预览路由 */
export const PROGRESSIVE_LENSES_COLLECTION_PATH = '/5a77926c6bfede91';
/** 变色镜片专题页预览路由 */
export const PHOTOCHROMIC_LENSES_COLLECTION_PATH = '/fc3a970133975bff';
/** 环保专题页预览路由 */
export const ECO_COLLECTION_PATH = '/bcded496e0d8521c';
/** 全视线专题页预览路由 */
export const TRANSITIONS_COLLECTION_PATH = '/963208fc7f127d6f';
/** 6周年庆预览路由 */
export const ANNVER_PREVIEW_PATH = '/fa3997112f153c93f79e1c4ca7c8b1a8';
/** 自定义专题页预览路由 */
export const CUSTOM_THEME_PATH = '/57ced81852bfdf50/collections';
/** 新品专题页预览路由 */
export const NEW_EYE_GLASSES_PATH = '/collections/ef6f22f514e97e11';
/** 自定义新品专题页预览路由 */
export const CUSTOM_NEW_EYE_GLASSES_PATH = '/campaigns/ef6f22f514e97e11';
/** 毫米转英寸 */
export const MM_TO_IN = 0.03937;

/** 展示的镜片类型配置列表 */
export const availableLensesAll: AvailableLensItem[] = [
  {
    name: 'Blue Light Blocking',
    shortName: 'Blue Light Blocking',
    imgClass: 'pc-lens01',
    desc: 'Everyday protection with special technology that reduces exposure to blue-violet light.Highly recommended for those using electronic devices daily to relieve eye fatigue.',
    link: '/blue-light-glasses',
  },
  {
    name: 'Photochromic Lenses',
    shortName: 'Photochromic',
    imgClass: 'pc-lens02',
    desc: 'Light-adaptive lenses that are clear indoors and darken automatically outdoors.',
    link: '/photochromic-lenses',
  },
  {
    name: 'Polarized Sunglass Lenses',
    shortName: 'Polarized',
    imgClass: 'pc-lens03',
    desc: 'Turn virtually any frame into sunglasses with our wide array of tint options.',
    link: '',
  },
];

/**
 * 根据当前排序与广告位参数，得到接口所需的 order / top_shop_skus / adp
 * @param sortValue 当前排序方式（如用户选择的排序）
 * @param adp 逗号分隔的置顶 SKU 字符串（广告位排序时存在）
 * @returns 标准排序参数对象
 */
export function transformAdp(sortValue: string, adp: string | undefined): TransformAdpResult {
  let top_shop_skus: string | string[] = adp ?? '';
  let order = sortValue ?? '';

  if (order && order !== 'recommend') {
    return {
      order,
      top_shop_skus: '',
      adp,
    };
  }

  if (adp?.length) {
    top_shop_skus = adp.split(',');
    if (top_shop_skus.length) {
      order = 'sort_by_shop_skus';
    }
  }

  return {
    order,
    top_shop_skus,
    adp,
  };
}
